/**
 * <announcement-bar> web component.
 * Marquee animation is pure CSS — JS only handles:
 *   • Sticky-bar height tracking (CSS variable for downstream sticky offsets)
 *   • Moving links + localization between the desktop bar and mobile menu on breakpoint change
 */

if (!customElements.get('announcement-bar')) {
  class AnnouncementBar extends HTMLElement {
    constructor() {
      super();
      this.localization = this.querySelector('.announcement__localization');
      this.links = this.querySelectorAll('.js-announcement-link');
      this.menu = document.querySelector('.main-menu__content');
      this.isSticky = this.dataset.isSticky === 'true';
    }

    connectedCallback() {
      if (this.isSticky) {
        this.closest('.cc-announcement')?.classList.add('announcement--sticky');
      }

      this.updateHeight();
      this.resizeObserver = new ResizeObserver(() => this.updateHeight());
      this.resizeObserver.observe(this);

      if (this.links.length) {
        this.boundMoveLinks = this.moveLinks.bind(this);
        window.addEventListener('on:breakpoint-change', this.boundMoveLinks);
        this.moveLinks();
      }

      if (this.localization) {
        this.boundMoveLocalization = this.moveLocalization.bind(this);
        window.addEventListener('on:breakpoint-change', this.boundMoveLocalization);
        this.moveLocalization();
      }
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      if (this.boundMoveLinks) {
        window.removeEventListener('on:breakpoint-change', this.boundMoveLinks);
      }
      if (this.boundMoveLocalization) {
        window.removeEventListener('on:breakpoint-change', this.boundMoveLocalization);
      }
    }

    /** Update the global --announcement-bar-height var so sticky page elements offset correctly. */
    updateHeight() {
      const value = this.isSticky ? `${this.offsetHeight}px` : '0px';
      document.body.style.setProperty('--announcement-bar-height', value);
    }

    /** Relocate announcement links into the mobile nav at small breakpoints; restore on desktop. */
    moveLinks() {
      if (!this.menu) return;
      const existing = document.querySelector('.mob__announcement-links');
      const isDesktop = window.theme?.mediaMatches?.md;

      if (!isDesktop && !existing && this.links.length) {
        const nav = document.createElement('nav');
        nav.classList.add('mob__announcement-links', 'secondary-nav');
        this.links.forEach((link) => {
          const li = document.createElement('li');
          const a = document.createElement('a');
          a.className = 'secondary-nav__item';
          a.href = link.href;
          a.textContent = link.innerText;
          li.appendChild(a);
          nav.appendChild(li);
        });
        this.menu.appendChild(nav);
      } else if (isDesktop && existing) {
        existing.remove();
      }
    }

    /** Relocate localization controls into the mobile nav at small breakpoints; restore on desktop. */
    moveLocalization() {
      if (!this.menu || !this.localization) return;
      const existing = document.querySelector('.mob__localization');
      const isDesktop = window.theme?.mediaMatches?.md;

      if (!isDesktop && !existing && this.localization.firstElementChild) {
        const wrapper = document.createElement('div');
        wrapper.classList.add('mob__localization');
        wrapper.appendChild(this.localization.firstElementChild);
        this.menu.appendChild(wrapper);
      } else if (isDesktop && existing) {
        this.localization.appendChild(existing.firstElementChild);
        existing.remove();
      }
    }
  }

  customElements.define('announcement-bar', AnnouncementBar);
}
